/**
 * =============================================================================
 * saveBlob — hand a downloaded file to the operator, or say why not
 * =============================================================================
 *
 * ⚠ THE TRAP THIS EXISTS TO AVOID.
 *
 * Every export in the CMS is requested with `responseType: 'blob'`, which means
 * a FAILED response arrives as a Blob too — the error JSON, or an IIS error
 * page, wrapped in exactly the same type as a successful spreadsheet. The
 * existing handlers test `data instanceof Blob`, which is true either way, so
 * a 401 or a 500 was saved to disk as `ResearchesTable.xlsx` and handed over.
 * The operator then gets a file Excel refuses to open, with nothing anywhere
 * explaining why.
 *
 * So the type is checked, not just the shape. A spreadsheet or a zip is saved;
 * anything else is read back as text and surfaced as a message.
 */

/** Media types an export is legitimately allowed to be. */
const EXPORT_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/zip',
  'application/x-zip-compressed',
  'application/octet-stream',
];

/**
 * Pull a human reason out of an error body that arrived as a Blob.
 *
 * The API's envelope puts the user-facing reason in `error`, and the contract
 * says it beats `message`. A non-JSON body (an IIS page) yields nothing, and
 * the caller's fallback is used.
 *
 * @param {Blob} blob
 * @returns {Promise<string>} the reason, or '' when there is none to find
 */
export const readBlobError = async (blob) => {
  try {
    const text = await blob.text();
    try {
      const body = JSON.parse(text);
      return body?.error || body?.message || '';
    } catch {
      // Not JSON. An HTML error page is not something to show an operator.
      return '';
    }
  } catch {
    return '';
  }
};

/**
 * Save a successful export, or report why it is not one.
 *
 * @param {object}   options
 * @param {*}        options.data       the axios response body
 * @param {string}   options.fileName   including extension
 * @param {Function} [options.onError]  called with a human message
 * @returns {Promise<boolean>} whether a file was actually saved
 */
export const saveBlob = async ({ data, fileName, onError }) => {
  if (!(data instanceof Blob)) {
    onError?.('تعذر تنزيل الملف');
    return false;
  }

  // An empty body is not a spreadsheet, whatever its type claims.
  if (data.size === 0) {
    onError?.('الملف المستلم فارغ');
    return false;
  }

  const type = (data.type || '').split(';')[0].trim().toLowerCase();
  if (type && !EXPORT_TYPES.includes(type)) {
    const reason = await readBlobError(data);
    onError?.(reason || 'تعذر تنزيل الملف');
    return false;
  }

  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked on the next tick: revoking synchronously after `click()` can race
  // the browser's own read of the object URL in some versions.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
  return true;
};

/**
 * `الأبحاث-2026-10-16.xlsx` — dated, so two exports taken on different days do
 * not overwrite each other in the operator's downloads folder.
 */
export const exportFileName = (base, { zip } = {}) => {
  const today = new Date().toISOString().slice(0, 10);
  return `${base}-${today}.${zip ? 'zip' : 'xlsx'}`;
};

export default saveBlob;
